import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Globe, Layout, Code, Smartphone, Search } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import AIProjectConsultant from "../components/AIProjectConsultant";

const SERVICES = [
  { label: 'Web Development', to: '/services/web-development', icon: Code, desc: 'Custom high-performance websites.' },
  { label: 'UI/UX Design', to: '/services/ui-ux-design', icon: Layout, desc: 'Stunning user interfaces.' },
  { label: 'App Development', to: '/services/app-development', icon: Smartphone, desc: 'iOS & Android applications.' },
  { label: 'SEO Optimization', to: '/services/seo', icon: Search, desc: 'Climb search rankings.' },
];

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'About Us', to: '/about' },
  { label: 'Portfolio', to: '/portfolio' },
  { label: 'Blog', to: '/blog' },
  { label: 'Contact', to: '/contact' },
];

export default function Navbar() {
  const [showConsultant, setShowConsultant] = useState(false);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 800);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMegaMenuOpen(false); window.scrollTo(0, 0); }, [location.pathname]);
  useEffect(() => { document.body.style.overflow = open ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [open]);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-40 transition-[box-shadow,border-radius,background-color] duration-300 bg-[#0a0a0a] border-b border-white/10 will-change-transform [transform:translateZ(0)] [backface-visibility:hidden] ${scrolled ? 'shadow-lg rounded-b-[25px]' : ''}`}>
        <div className="container-max flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5" aria-label="CodeViora home">
            <img src={require('../images/Codeviora.webp')} alt="CodeViora Logo" className="h-8 w-auto object-contain" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 relative" aria-label="Main navigation">
            <NavLink to="/" end className={({ isActive }) => `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'text-white bg-white/10' : 'text-white/50 hover:text-white hover:bg-white/5 dark:bg-white/5'}`}>
              Home
            </NavLink>

            {/* Mega Menu Trigger */}
            <div 
              className="relative"
              onMouseEnter={() => setMegaMenuOpen(true)}
              onMouseLeave={() => setMegaMenuOpen(false)}
            >
              <button className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-white/50 hover:text-white hover:bg-white/5 dark:bg-white/5 transition-colors">
                Services <ChevronDown size={14} className={`transition-transform duration-200 ${megaMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Mega Menu Dropdown */}
              <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 w-[500px] ${megaMenuOpen ? 'opacity-100 translate-y-0 visible' : 'opacity-0 translate-y-2 invisible'}`}>
                <div className="bg-[#111] border border-white/10 rounded-2xl shadow-2xl p-4 grid grid-cols-2 gap-2">
                  <Link to="/services" className="col-span-2 px-4 py-2 text-sm font-medium text-white hover:bg-white/5 dark:bg-white/5 rounded-xl transition-colors mb-2">View All Services &rarr;</Link>
                  {SERVICES.map((srv) => {
                    const Icon = srv.icon;
                    return (
                      <Link key={srv.to} to={srv.to} className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 dark:bg-white/5 transition-colors group">
                        <div className="w-10 h-10 rounded-lg bg-white/5 dark:bg-white/5 flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                          <Icon size={18} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white mb-0.5">{srv.label}</div>
                          <div className="text-xs text-white/50 line-clamp-2">{srv.desc}</div>
                        </div>
                      </Link>
                    )
                  })}
                </div>
              </div>
            </div>

            {NAV_LINKS.slice(1).map(({ label, to }) => (
              <NavLink key={to} to={to} className={({ isActive }) => `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'text-white bg-white/10' : 'text-white/50 hover:text-white hover:bg-white/5 dark:bg-white/5'}`}>
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle />
                    <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              setShowConsultant(true);
            }}
            className="btn-primary-inv py-2 flex items-center"
          >
            <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3z"/>
          </svg> AI Consultant
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 8H13M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <ThemeToggle />

           <button
            onClick={() => setShowConsultant(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-[11px] font-medium transition-colors whitespace-nowrap"
          >
              <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3z"/>
            </svg> AI
            </button>

            <button
              onClick={() => setOpen(true)}
              className="w-10 h-10 flex flex-col items-center justify-center gap-1.5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <span className="w-5 h-0.5 bg-white rounded-full self-end"></span>
              <span className="w-4 h-0.5 bg-white rounded-full self-end"></span>
              <span className="w-3 h-0.5 bg-white/50 rounded-full self-end"></span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      <div className={`fixed inset-0 z-50 md:hidden transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 sidebar-overlay bg-black/80 dark:bg-white/80" onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 bottom-0 w-72 bg-[#0a0a0a] border-l border-white/10 flex flex-col transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
            <span className="text-white text-lg" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>CODEVIORA</span>
            <button onClick={() => setOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/60">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-4 py-6 flex flex-col gap-1">
            <NavLink to="/" end onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors ${isActive ? 'text-white bg-white/10' : 'text-white/50 hover:bg-white/5 dark:bg-white/5 hover:text-white'}`}>
              Home
            </NavLink>
            <div className="px-4 py-2 mt-2 mb-1 text-xs font-medium text-white/40 uppercase tracking-wider">Services</div>
            {SERVICES.map((srv) => {
              const Icon = srv.icon;
              return (
                <Link key={srv.to} to={srv.to} onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/50 hover:bg-white/5 dark:bg-white/5 hover:text-white transition-colors">
                  <Icon size={16} /> {srv.label}
                </Link>
              )
            })}
            <div className="px-4 py-2 mt-2 mb-1 text-xs font-medium text-white/40 uppercase tracking-wider">Pages</div>
            {NAV_LINKS.slice(1).map(({ label, to }) => (
              <NavLink key={to} to={to} onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors ${isActive ? 'text-white bg-white/10' : 'text-white/50 hover:bg-white/5 dark:bg-white/5 hover:text-white'}`}>
                {label}
              </NavLink>
            ))}
          </nav>
        <div className="px-6 py-6 border-t border-white/10">
        <button
          onClick={() => {
            setOpen(false);
            setShowConsultant(true);
          }}
          className="btn-primary-inv w-full justify-center"
        >
          ✨ AI Consultant
        </button>
        </div>
        </div>
      </div>
            {showConsultant && (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center p-5">

    <div
      className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      onClick={() => setShowConsultant(false)}
    />

    <div className="relative w-full max-w-2xl  bg-[#0a0a0a] rounded-3xl overflow-hidden border border-white/10">

      <button
        onClick={() => setShowConsultant(false)}
        className="absolute top-5 right-5 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white"
      >
        ✕
      </button>

      <div className="h-full overflow-y-auto">
        <AIProjectConsultant onClose={() => setShowConsultant(false)} />
      </div>

    </div>

  </div>
)}
    </>
  );
}
